// SPDX-License-Identifier: GPL-3.0-only
// Source: OpenSaga 7897237, gizmo/object/gizmopickups.cpp AddPickups,
// render/fx/parts.cpp MakePartVector/AddCoinsAsParts/PartCollide/PartStop_Coin,
// gizmos/fx/gizmopickups.cpp and gizmo/object/gizmopickup.cpp coin table.
// Supported: ordinary ground-area coin rewards. Terrain sweep is supplied by
// the host; missing terrain never invents a floor. Vehicle motion, heart
// release, panel coin messages and shared particle replacement are unresolved.
import { pickupCollision } from './pickups.mjs';

const COINS = Object.freeze([
  { code: 's', score: 10, model: 'silver_pan_coin' }, { code: 'g', score: 100, model: 'gold_pan_coin' },
  { code: 'b', score: 1000, model: 'blue_pan_coin' }, { code: 'p', score: 10000, model: 'purple_pan_coin' },
]);
const TAU = Math.PI * 2;
function uint(value, name, max = 0xffffffff) { if (!Number.isInteger(value) || value < 0 || value > max) throw new Error(`Invalid ${name}.`); return value; }
function vector(value) { const a = Array.isArray(value) ? value : [value?.x, value?.y, value?.z]; if (a.length !== 3 || !a.every(Number.isFinite)) throw new Error('Invalid reward vector.'); return a.slice(); }
const object = p => ({ x: p[0], y: p[1], z: p[2] });
const angle = a => (Math.trunc(a) & 65535) * TAU / 65536;
const rotateX = (p, a) => { const c = Math.cos(angle(a)), s = Math.sin(angle(a)); return [p[0], p[1] * c - p[2] * s, p[1] * s + p[2] * c]; };
const rotateY = (p, a) => { const c = Math.cos(angle(a)), s = Math.sin(angle(a)); return [p[0] * c + p[2] * s, p[1], p[2] * c - p[0] * s]; };
const rotateZ = (p, a) => { const c = Math.cos(angle(a)), s = Math.sin(angle(a)); return [p[0] * c - p[1] * s, p[0] * s + p[1] * c, p[2]]; };

export function partitionReward(amount, { areaFlags = 0, consolidate = true, random16 = () => 0 } = {}) {
  uint(amount, 'reward amount', 0x7fffffff); uint(areaFlags, 'area flags');
  let remaining = Math.min(amount - amount % 10, 512000);
  const counts = [0, 0, 0, 0];
  for (let i = 3; i >= 0; i--) { counts[i] = Math.floor(remaining / COINS[i].score); remaining %= COINS[i].score; }
  const random = () => uint(random16(), 'random16', 65535);
  // These intentionally change nominal value. They are source consolidation
  // thresholds, not a value-preserving denomination exchange.
  if (!(areaFlags & 0x104) && consolidate) {
    if (counts[0] > 1) { counts[0] = 0; if (++counts[1] > 9) { counts[1] %= 10; if (++counts[2] > 9) { counts[2] %= 10; counts[3]++; } } }
    if (counts[1] > 4) { counts[1] = Math.trunc(random() / 32768); if (++counts[2] > 9) { counts[2] %= 10; counts[3]++; } }
    if (counts[2] > 5) { counts[2] = Math.trunc(random() / 65536); counts[3]++; }
  }
  return counts.map((count, i) => ({ ...COINS[i], count }));
}

export function deathStudLoss(studs, { cause = 1, bonusArea = false, adjustment = 0, invincible = false, immuneCharacter = false, hasCoinPacket = true, arcadeNoLoss = false, scoreMultiplierActive = false } = {}) {
  uint(studs, 'run studs'); if (![1, 2].includes(cause) || ![-1, 0, 1].includes(adjustment)) throw new Error('Invalid stud loss context.');
  if (invincible || immuneCharacter || !hasCoinPacket || arcadeNoLoss) return { lost: 0, dropped: 0, remaining: studs };
  // TouchHacks.cpp:36-41. OpenSaga Android falls lose zero; PC fall parity is
  // not established. Callers must label this source rule rather than infer PC.
  let loss = cause === 1 ? bonusArea ? 10000 : 1000 : 0;
  if (adjustment === 1) loss *= 2; else if (adjustment === -1) loss = Math.trunc(loss / 2);
  const lost = Math.min(studs, loss);
  return { lost, dropped: scoreMultiplierActive ? 0 : lost, remaining: studs - lost };
}

export function createRewardRuntime({ areaFlags = 0, areaScale = 1, gravity = -6, random16 = () => Math.floor(Math.random() * 65536), modelAvailable = () => false, flightCapacity = 64 } = {}) {
  uint(areaFlags, 'area flags'); uint(flightCapacity, 'particle capacity', 65536);
  if (!(areaScale > 0) || !Number.isFinite(areaScale) || !Number.isFinite(gravity) || typeof random16 !== 'function' || typeof modelAvailable !== 'function') throw new Error('Invalid reward runtime.');
  const flights = [], resting = Array(64).fill(null), events = [], blocked = [];
  let nextId = 0, nextTemporary = 0, frame = 0;
  const random = () => uint(random16(), 'random16', 65535);
  function collect(coin, actor, reason) { coin.active = false; events.push({ type: 'collect', id: coin.canonicalId, drawId: coin.id, rewardId: coin.rewardId, typeCode: coin.code, score: coin.score, modelName: coin.modelName, position: coin.position.slice(), playerIndex: actor.playerIndex ?? 0, reason }); }
  function makeVelocity(direction, speed) {
    if (!direction) return [(random() * 2 / 65535 - 1) * areaScale, (random() / 65535 + 1) * areaScale, (random() * 2 / 65535 - 1) * areaScale];
    const x = Math.trunc(Math.atan2(direction[2], direction[1]) * 65536 / TAU);
    const rotated = rotateX(direction, -x), z = -Math.trunc(Math.atan2(rotated[0], rotated[1]) * 65536 / TAU);
    const zr = random(), xr = random();
    return rotateX(rotateZ([0, 1, 0], z + Math.trunc(zr / 6) - 0x1555), x + Math.trunc(xr / 6) - 0x1555).map(n => n * speed);
  }
  function temporary(coin, actor) {
    const previous = resting[nextTemporary];
    if (previous?.active && actor?.playerActive !== false && actor) collect(previous, actor, 'temporary-ring-replacement');
    resting[nextTemporary] = { ...coin, id: `temporary:${++nextId}`, active: true, phase: 'resting', remaining: 5, rotation: 0, drawn: false };
    nextTemporary = (nextTemporary + 1) % 64; coin.active = false;
  }
  return {
    spawnReward(event) {
      if (!event || !['force', 'build', 'obstacle', 'death'].includes(event.kind)) throw new Error('Unsupported reward source.');
      if (typeof event.id !== 'string' || !event.id || event.id.length > 512) throw new Error('Missing reward release identity.');
      const position = vector(event.position); uint(event.amount, 'reward amount', 0x7fffffff);
      if (gravity === 0 || (areaFlags & 0x100) || event.toPanel || event.challengeMode || event.missionActive) {
        blocked.push({ rewardId: event.id, reason: 'unsupported vehicle/panel or disabled reward context' }); return [];
      }
      let direction = null, speed = areaScale;
      if (event.kind !== 'death') {
        if (!Number.isInteger(event.rotationX) || !Number.isInteger(event.rotationY)) throw new Error('Missing original reward rotation.');
        direction = rotateY(rotateX([0, 1, 0], event.rotationX), event.rotationY);
        speed = event.kind === 'build' ? 1.75 : event.activationRadius;
        if (!Number.isFinite(speed) || speed < 0) throw new Error('Missing original Force reward speed.');
        if (speed === 1) speed = areaScale;
      }
      const spawned = []; let ordinal = 0;
      for (const type of partitionReward(event.amount, { areaFlags, consolidate: event.consolidate ?? event.kind !== 'death', random16: random })) for (let i = 0; i < type.count; i++) {
        const canonicalId = `${event.id}:coin:${ordinal++}`;
        const velocity = makeVelocity(direction, speed), variant = Math.trunc(random() / 16384), modelName = `${type.model}${variant + 1}`;
        if (!modelAvailable(modelName)) { blocked.push({ rewardId: event.id, reason: 'original coin model unavailable', modelName }); continue; }
        if (flights.filter(p => p.active).length >= flightCapacity) { blocked.push({ rewardId: event.id, reason: 'shared particle replacement not implemented' }); continue; }
        const coin = { id: `flying:${++nextId}`, canonicalId, rewardId: event.id, code: type.code, score: type.score, modelName, variant, position: position.slice(), velocity, active: true, phase: 'flying', age: 0, remaining: 30, rotation: 0, scale: areaScale };
        flights.push(coin); spawned.push(coin.id);
      }
      return spawned;
    },
    update(dt, { actor = null, floor = null, drawn = () => false, miniCutCamera = false } = {}) {
      if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid reward time step.');
      if (floor !== null && typeof floor !== 'function') throw new Error('Invalid terrain sweep callback.');
      frame++;
      // Host floor callback receives start, velocity, gravity, duration and
      // radius; returns null or exact first impact {time,position,normal}.
      // Native CastPart terrain tracing is not replaced with guessed floor Y.
      for (const coin of flights) {
        if (!coin.active) continue;
        coin.age = Math.fround(coin.age + dt);
        let moveTime = dt;
        const hit = floor?.({ position: coin.position.slice(), velocity: coin.velocity.slice(), gravity, duration: dt, radius: .045 * areaScale }) ?? null;
        if (hit) {
          if (!Number.isFinite(hit.time) || hit.time < 0 || hit.time > dt) throw new Error('Invalid coin terrain impact time.');
          const normal = vector(hit.normal), magnitude = Math.hypot(...normal);
          if (Math.abs(magnitude - 1) > 1e-4) throw new Error('Invalid coin terrain normal.');
          coin.position = vector(hit.position); moveTime -= hit.time;
          const dot = normal.reduce((sum, n, i) => sum + n * coin.velocity[i], 0);
          coin.velocity = coin.velocity.map((v, i) => v - 2 * dot * normal[i]);
          coin.velocity[0] *= .75; coin.velocity[2] *= .75;
          if (normal[1] > .5) {
            coin.velocity[1] *= .75;
            if (coin.velocity.reduce((sum, n) => sum + n * n, 0) < .1) { temporary(coin, actor); continue; }
          }
        }
        for (let axis = 0; axis < 3; axis++) coin.position[axis] += coin.velocity[axis] * moveTime;
        coin.velocity[1] += gravity * moveTime;
        if (actor && actor.playerActive !== false && actor.playerControlled !== false && !actor.collectDisabled && coin.age >= Math.fround(.1)) {
          const min = vector(actor.collisionMin), max = vector(actor.collisionMax), radius = .045 * areaScale;
          if (coin.position.every((v, i) => min[i] <= v + radius && max[i] >= v - radius)) collect(coin, actor, 'flying-collision');
        }
        coin.remaining -= dt; if (coin.remaining <= 0) coin.active = false;
      }
      // The original Collide returns at most one resting pickup per call.
      let collectedResting = false;
      for (const coin of resting) {
        if (!coin?.active) continue;
        coin.rotation = (coin.rotation + Math.trunc(16384 * dt)) & 65535;
        coin.drawn = !!drawn(coin.id); if (!miniCutCamera) coin.remaining -= dt;
        if (coin.remaining < 0) { coin.active = false; continue; }
        if (actor && actor.playerActive !== false && !collectedResting && pickupCollision({ ...coin, enabled: true, collected: false, typeEnabled: true, configFlags: 0, effectiveTypeCode: coin.code, collisionPosition: object(coin.position), pickupScale: areaScale }, actor)) {
          collect(coin, actor, 'temporary-collision'); collectedResting = true;
        }
      }
      for (let i = flights.length - 1; i >= 0; i--) if (!flights[i].active) flights.splice(i, 1);
      return events.splice(0);
    },
    drawStates() { return [...flights, ...resting.filter(Boolean)].filter(c => c.active).map(c => ({ id: c.id, canonicalId: c.canonicalId, rewardId: c.rewardId, phase: c.phase, modelName: c.modelName, position: c.position.slice(), rotation: c.rotation * TAU / 65536, scale: areaScale, visible: c.phase !== 'resting' || c.remaining > .5 || frame % 6 < 3 })); },
    drainBlocked() { return blocked.splice(0); },
    reset() { flights.length = 0; resting.fill(null); events.length = 0; blocked.length = 0; nextTemporary = 0; },
    limitations: ['shared particle pool replacement', 'general CastPart terrain tracing supplied by host', 'flying particle rotation', 'heart release', 'panel coin messages', 'vehicle pickups', 'PC fall-loss rules'],
  };
}
